// New Client Onboarding Wizard Page

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { WizardStepIndicator } from '@/components/onboarding/WizardStepIndicator';
import { OnboardingWizardProvider } from '@/components/onboarding/OnboardingWizardContext';
import { Step1ClientProfile } from '@/components/onboarding/steps/Step1ClientProfile';
import { Step2PayerSelection } from '@/components/onboarding/steps/Step2PayerSelection';
import { Step3EpicIntegration } from '@/components/onboarding/steps/Step3EpicIntegration';
import { Step4RulesetTemplates } from '@/components/onboarding/steps/Step4RulesetTemplates';
import { Step5SLADefaults } from '@/components/onboarding/steps/Step5SLADefaults';
import { Step6ReviewActivate } from '@/components/onboarding/steps/Step6ReviewActivate';
import { useClientOnboarding } from '@/hooks/useClientOnboarding';
import { useClients } from '@/hooks/useClientSettings';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export default function ClientOnboardingWizard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientIdParam = searchParams.get('clientId');
  
  const [selectedClientId, setSelectedClientId] = useState<string | null>(clientIdParam);
  const [currentStep, setCurrentStep] = useState(1);
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientType, setNewClientType] = useState<'HOSPITAL' | 'SURGICAL_PRACTICE'>('HOSPITAL');
  const [isCreating, setIsCreating] = useState(false);
  
  const { clients, createClient, isCreating: isCreatingClient } = useClients();
  const { 
    progress, 
    isLoading, 
    initializeOnboarding, 
    isInitializing 
  } = useClientOnboarding(selectedClientId || undefined);

  // Get incomplete clients for selection
  const incompleteClients = clients.filter(c => !c.is_active);

  // Update URL when client changes
  useEffect(() => {
    if (selectedClientId) {
      setSearchParams({ clientId: selectedClientId });
    }
  }, [selectedClientId, setSearchParams]);

  // Update step from progress
  useEffect(() => {
    if (progress) {
      setCurrentStep(progress.current_step);
    }
  }, [progress]);

  // Create new client
  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      toast.error('Client name is required');
      return;
    }

    setIsCreating(true);
    try {
      // Create the client
      const { data, error } = await supabase
        .from('clients')
        .insert({
          client_name: newClientName,
          client_type: newClientType,
          is_active: false,
        })
        .select()
        .single();

      if (error) throw error;

      // Onboarding progress table removed — skip initialization
      console.debug('Skipping client_onboarding_progress (table removed)');

      // Also create client_settings entry
      await supabase
        .from('client_settings')
        .insert({
          client_id: data.id,
        });

      setSelectedClientId(data.id);
      setShowNewClientDialog(false);
      setNewClientName('');
      toast.success('Client created successfully');
    } catch (error: any) {
      toast.error(`Failed to create client: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  // Get completed steps array
  const getCompletedSteps = () => {
    if (!progress) return [];
    const completed: number[] = [];
    if (progress.step1_complete) completed.push(1);
    if (progress.step2_complete) completed.push(2);
    if (progress.step3_complete) completed.push(3);
    if (progress.step4_complete) completed.push(4);
    if (progress.step5_complete) completed.push(5);
    if (progress.step6_complete) completed.push(6);
    return completed;
  };

  // Check if can navigate to step
  const canNavigateToStep = (step: number) => {
    if (step === 1) return true;
    const completed = getCompletedSteps();
    for (let i = 1; i < step; i++) {
      if (!completed.includes(i)) return false;
    }
    return true;
  };

  // Handle step navigation
  const goToStep = (step: number) => {
    if (canNavigateToStep(step)) {
      setCurrentStep(step);
    }
  };

  // Render step content
  const renderStep = () => {
    if (!selectedClientId || !progress) return null;

    const commonProps = {
      clientId: selectedClientId,
      progress,
      onComplete: () => setCurrentStep(prev => Math.min(prev + 1, 6)),
      onBack: () => setCurrentStep(prev => Math.max(prev - 1, 1)),
    };

    switch (currentStep) {
      case 1:
        return <Step1ClientProfile {...commonProps} />;
      case 2:
        return <Step2PayerSelection {...commonProps} />;
      case 3:
        return <Step3EpicIntegration {...commonProps} />;
      case 4:
        return <Step4RulesetTemplates {...commonProps} />;
      case 5:
        return <Step5SLADefaults {...commonProps} />;
      case 6:
        return <Step6ReviewActivate {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <OpsLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/client-settings')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">New Client Onboarding</h1>
              <p className="text-muted-foreground">
                Configure all required settings before go-live
              </p>
            </div>
          </div>
        </div>

        {/* Client Selection or Create */}
        {!selectedClientId && (
          <Card>
            <CardHeader>
              <CardTitle>Select or Create Client</CardTitle>
              <CardDescription>
                Choose an existing client to continue onboarding, or create a new one.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {incompleteClients.length > 0 && (
                <div className="space-y-3">
                  <Label>Continue Onboarding</Label>
                  <div className="grid gap-2">
                    {incompleteClients.map(client => (
                      <Button
                        key={client.id}
                        variant="outline"
                        className="justify-start h-auto py-3"
                        onClick={() => setSelectedClientId(client.id)}
                      >
                        <div className="text-left">
                          <div className="font-medium">{client.client_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {client.client_type} • Created {new Date(client.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <Dialog open={showNewClientDialog} onOpenChange={setShowNewClientDialog}>
                <DialogTrigger asChild>
                  <Button className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    Create New Client
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Client</DialogTitle>
                    <DialogDescription>
                      Enter the basic information to start the onboarding process.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="client-name">Client Name</Label>
                      <Input
                        id="client-name"
                        placeholder="Cincinnati Orthopedic Associates"
                        value={newClientName}
                        onChange={(e) => setNewClientName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="client-type">Client Type</Label>
                      <Select
                        value={newClientType}
                        onValueChange={(v) => setNewClientType(v as 'HOSPITAL' | 'SURGICAL_PRACTICE')}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="HOSPITAL">Hospital</SelectItem>
                          <SelectItem value="SURGICAL_PRACTICE">Surgical Practice</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowNewClientDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateClient} disabled={isCreating}>
                      {isCreating ? 'Creating...' : 'Create & Start Onboarding'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        )}

        {/* Wizard Content */}
        {selectedClientId && (
          <>
            {isLoading ? (
              <Card>
                <CardContent className="py-10">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                  </div>
                </CardContent>
              </Card>
            ) : progress ? (
              <OnboardingWizardProvider clientId={selectedClientId} progress={progress}>
                <div className="space-y-8">
                  {/* Step Indicator */}
                  <Card className="p-6">
                    <WizardStepIndicator
                      currentStep={currentStep}
                      completedSteps={getCompletedSteps()}
                      onStepClick={goToStep}
                      canNavigate={canNavigateToStep}
                    />
                  </Card>

                  {/* Step Content */}
                  {renderStep()}
                </div>
              </OnboardingWizardProvider>
            ) : (
              <Card>
                <CardContent className="py-10 text-center">
                  <p className="text-muted-foreground mb-4">
                    No onboarding progress found for this client.
                  </p>
                  <Button 
                    onClick={() => initializeOnboarding(selectedClientId)}
                    disabled={isInitializing}
                  >
                    {isInitializing ? 'Initializing...' : 'Start Onboarding'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </OpsLayout>
  );
}
